(function () {
  'use strict';

  // ============================================================
  // BRAND CONFIG — the values most likely to change per brand.
  // Jockey NZ: shipping/returns facts per jockey.co.nz's own shipping
  // policy copy (member free shipping is live; non-members free over
  // $49, $10 under; 3-5 business day delivery; returns exclude briefs
  // and hosiery unless faulty), confirmed 2026-09-18. Visual identity
  // (fonts/colors/radius) carried over unchanged from Jockey AU — same
  // brand, same theme family.
  // ============================================================
  const BRAND = {
    fontBody: '"Nunito Sans", sans-serif',
    fontHeading: '"Montserrat", sans-serif',
    fontWeightHeading: '400',
    colorText: '#001135',
    colorBorder: '#e2e4e8',
    colorBg: '#f5f5f5',
    accent: '#001135', // check-icon + asterisk-link color; distinct from colorText for brands with a separate accent
    radius: '0px', // container/panel corner radius — 0 for sharp-cornered brands, higher for rounded ones
    titleTextTransform: 'none',
    titleLetterSpacing: '0',
    freeShippingThreshold: '$49+',
    deliveryBusinessDays: 5,
    returnsWindowDays: 30,
    returnsPolicyUrl: 'https://jockey.co.nz/pages/returns-and-exchanges',
    shippingOptionsUrl: 'https://jockey.co.nz/pages/shipping-options',
  };

  // Retries a single DOM query-and-bail against late theme-JS hydration —
  // running from document-start means DOMContentLoaded can fire before the
  // buy box / accordion have finished rendering, unlike the old
  // document-idle timing this feature originally shipped under.
  function pocRetryUntil(check, { tries = 20, interval = 250 } = {}) {
    let attempts = 0;
    (function tick() {
      if (check() || ++attempts >= tries) return;
      setTimeout(tick, interval);
    })();
  }

  function init() {
    injectTrustBoxStyle();
    injectLightboxStyle();
    pocRetryUntil(() => {
      injectTrustBox();
      return !!document.getElementById('poc-trust-box');
    });
    pocRetryUntil(() => {
      rewriteShippingReturnsPanel();
      return !!document.getElementById('poc-shipping-returns-copy');
    });
  }

  // ---------- Trust points box ----------

  const TRUST_BOX_CSS = `
    #poc-trust-box{
      display: flex;
      font-family: ${BRAND.fontBody};
      margin-top: 8px;
      border: 1px solid ${BRAND.colorBorder};
      background: ${BRAND.colorBg};
      border-radius: ${BRAND.radius};
      overflow: hidden;
    }
    #poc-trust-box .poc-trust-cell{
      display: flex;
      align-items: center;
      /* flex-start (not center) lines every icon up at the same starting
         position regardless of how much its own text wraps — see
         jockey-pdp-suite.user.js v1.3.1 history in POCs.md for why. */
      justify-content: flex-start;
      gap: 8px;
      flex: 1;
      min-width: 0;
      padding: 5px 8px;
      border-left: 1px solid ${BRAND.colorBorder};
    }
    #poc-trust-box .poc-trust-cell:first-child{ border-left: none; }
    #poc-trust-box .poc-trust-icon{
      flex-shrink: 0;
      width: 22px;
      height: 22px;
      border-radius: 0;
      background: transparent;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    #poc-trust-box .poc-trust-icon svg{
      color: ${BRAND.accent};
    }
    #poc-trust-box .poc-trust-text{
      min-width: 0;
      text-align: left;
    }
    #poc-trust-box .poc-trust-title{
      font-size: 13px;
      font-family: ${BRAND.fontHeading};
      font-weight: ${BRAND.fontWeightHeading};
      color: ${BRAND.colorText};
      line-height: 1.2;
      text-transform: ${BRAND.titleTextTransform};
      letter-spacing: ${BRAND.titleLetterSpacing};
    }
    #poc-trust-box .poc-trust-detail{
      font-size: 12px;
      color: ${BRAND.colorText};
      line-height: 1.25;
    }
    #poc-trust-box .poc-trust-asterisk-link{
      color: inherit;
      text-decoration: underline;
      text-decoration-style: dotted;
      text-underline-offset: 2px;
    }
    .poc-dispatch-note{
      margin: 6px 0 0;
      font-size: 12px;
      color: ${BRAND.colorText};
      line-height: 1.35;
      text-align: center;
    }
    @media (max-width: 360px){
      #poc-trust-box{ flex-direction: column; }
      #poc-trust-box .poc-trust-cell{
        border-left: none;
        border-top: 1px solid ${BRAND.colorBorder};
        padding: 7px 10px;
      }
      #poc-trust-box .poc-trust-cell:first-child{ border-top: none; }
    }
  `;

  const CHECK_ICON =
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5 5L20 7"/></svg>';

  function injectTrustBoxStyle() {
    if (document.getElementById('poc-trust-box-style')) return;
    const style = document.createElement('style');
    style.id = 'poc-trust-box-style';
    style.textContent = TRUST_BOX_CSS;
    document.head.appendChild(style);
  }

  function injectTrustBox() {
    const paymentButtons = document.querySelector('.payment-buttons');
    if (!paymentButtons || document.getElementById('poc-trust-box')) return;
    // .payment-buttons' direct parent is a flex row; anchor to the block-level
    // .large-row ancestor instead so the new block renders on its own line below.
    const buttonRow = paymentButtons.closest('.large-row') || paymentButtons;

    const box = document.createElement('div');
    box.id = 'poc-trust-box';

    const columns = [
      {
        title: 'Free shipping',
        detail: `on orders ${BRAND.freeShippingThreshold}`,
      },
      {
        title: 'Delivery',
        detail: `by ${formatDeliveryDate()}`,
      },
      {
        title: 'Need it sooner?',
        detail: 'Express available at checkout',
      },
    ];

    columns.forEach((col) => {
      const cell = document.createElement('div');
      cell.className = 'poc-trust-cell';

      const icon = document.createElement('div');
      icon.className = 'poc-trust-icon';
      icon.innerHTML = CHECK_ICON;

      const text = document.createElement('div');
      text.className = 'poc-trust-text';

      const title = document.createElement('div');
      title.className = 'poc-trust-title';
      title.appendChild(document.createTextNode(col.title));

      const detail = document.createElement('div');
      detail.className = 'poc-trust-detail';
      detail.appendChild(document.createTextNode(col.detail));
      if (col.asterisk) {
        // The asterisk itself opens the returns policy in the same lightbox
        // used by the accordion links below, instead of being dead punctuation.
        const asteriskLink = document.createElement('a');
        asteriskLink.href = BRAND.returnsPolicyUrl;
        asteriskLink.className = 'poc-trust-asterisk-link';
        asteriskLink.textContent = '*';
        asteriskLink.setAttribute('aria-label', 'Returns and exchange policy details');
        interceptAsLightbox(asteriskLink, 'Returns & exchanges');
        detail.appendChild(asteriskLink);
      }

      text.appendChild(title);
      text.appendChild(detail);
      cell.appendChild(icon);
      cell.appendChild(text);
      box.appendChild(cell);
    });

    buttonRow.insertAdjacentElement('afterend', box);

    const dispatchNote = document.createElement('p');
    dispatchNote.className = 'poc-dispatch-note';
    dispatchNote.innerHTML = '<em>Next business day dispatch from Auckland</em>';
    box.insertAdjacentElement('afterend', dispatchNote);
  }

  // Adds N business days (Mon-Fri) to today, skipping weekends.
  function addBusinessDays(date, days) {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return result;
  }

  function formatDeliveryDate() {
    const target = addBusinessDays(new Date(), BRAND.deliveryBusinessDays);
    return target.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  }

  // ---------- On-page lightbox for policy pages ----------

  const LIGHTBOX_CSS = `
    #poc-lightbox-overlay{
      position: fixed;
      inset: 0;
      z-index: 9999;
      background: rgba(0, 17, 53, 0.5);
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: clamp(1rem, 4vw, 4rem) 1rem;
      overflow-y: auto;
    }
    #poc-lightbox-panel{
      background: #fff;
      width: min(760px, 100%);
      max-height: 100%;
      border-radius: ${BRAND.radius};
      box-shadow: none;
      display: flex;
      flex-direction: column;
      font-family: ${BRAND.fontBody};
    }
    #poc-lightbox-header{
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      padding: 1rem 1.25rem;
      border-bottom: 1px solid ${BRAND.colorBorder};
      flex: none;
    }
    #poc-lightbox-title{
      font-size: 1.05rem;
      font-family: ${BRAND.fontHeading};
      font-weight: ${BRAND.fontWeightHeading};
      color: ${BRAND.colorText};
      margin: 0;
      text-transform: ${BRAND.titleTextTransform};
      letter-spacing: ${BRAND.titleLetterSpacing};
    }
    #poc-lightbox-close{
      background: none;
      border: none;
      cursor: pointer;
      font-size: 1.4rem;
      line-height: 1;
      padding: .25rem .5rem;
      color: ${BRAND.colorText};
    }
    #poc-lightbox-body{
      padding: 1.25rem;
      overflow-y: auto;
    }
    #poc-lightbox-body.poc-lightbox-loading{
      color: ${BRAND.colorText};
      font-size: .95rem;
      padding: 2.5rem 1.25rem;
      text-align: center;
    }
  `;

  function injectLightboxStyle() {
    if (document.getElementById('poc-lightbox-style')) return;
    const style = document.createElement('style');
    style.id = 'poc-lightbox-style';
    style.textContent = LIGHTBOX_CSS;
    document.head.appendChild(style);
  }

  // Reuses the same scannable layout as poc-shipping-page.js in this folder
  // instead of showing the raw, un-styled fetched page inside the lightbox.
  // Kept as a literal copy (not a shared import) since these are standalone
  // scripts with no build step to share modules between them; the lightbox
  // header already shows "Shipping options" as the title, so the h1 is
  // omitted here.
  const SHIPPING_LIGHTBOX_CSS = `
    .poc-ship{ font-size: 1rem; }
    .poc-ship, .poc-ship *{ box-sizing: border-box; }
    .poc-ship-facts{ display: grid; grid-template-columns: repeat(2, 1fr); gap: 1px; list-style: none; margin: 0 0 1.5rem; padding: 0; border: 1px solid ${BRAND.colorBorder}; border-radius: ${BRAND.radius}; overflow: hidden; background: ${BRAND.colorBorder}; }
    .poc-ship-facts li{ padding: 1rem; background: #fff; margin: 0; }
    .poc-ship-facts strong{ display: block; font-size: 1rem; }
    .poc-ship-facts span{ display: block; font-size: .85rem; opacity: .68; margin-top: .2rem; }
    .poc-ship-cols{ display: flex; flex-direction: column; gap: 1rem; margin-bottom: 1.5rem; }
    .poc-ship-col{ border: 1px solid ${BRAND.colorBorder}; border-radius: ${BRAND.radius}; padding: 1rem 1.25rem; }
    .poc-ship-col h3{ margin: 0 0 .75rem; font-size: 1.15rem; }
    .poc-ship-lead{ margin: 0 0 .5rem; font-size: 1rem; line-height: 1.5; }
    .poc-ship-note{ margin: .5rem 0 0; font-size: .9rem; opacity: .68; line-height: 1.5; }
    table.poc-ship-rates{ width: 100%; border-collapse: collapse; font-size: 1rem; }
    table.poc-ship-rates th, table.poc-ship-rates td{ text-align: left; padding: .6rem .3rem; border-bottom: 1px solid rgba(127,127,127,.22); }
    table.poc-ship-rates th{ font-family: ${BRAND.fontHeading}; font-weight: 400; font-size: .75rem; letter-spacing: 0; text-transform: none; opacity: .75; }
    table.poc-ship-rates tr:last-child td{ border-bottom: none; }
    @media (max-width: 480px){
      .poc-ship-facts{ grid-template-columns: 1fr; }
      table.poc-ship-rates{ display: block; overflow-x: auto; white-space: nowrap; }
    }
  `;

  // NZ has a single flat rate per service (no under/over-threshold price
  // tiers like AU's Standard/Express split) — free shipping is a threshold
  // discount, not a second price column, so this table is Service/Time/Cost
  // rather than AU's Service/Time/Under-$X/Over-$X shape.
  const SHIPPING_LIGHTBOX_MARKUP = `
    <div class="poc-ship">
      <ul class="poc-ship-facts">
        <li><strong>Free for members</strong><span>or on orders over ${BRAND.freeShippingThreshold}</span></li>
        <li><strong>$10 under ${BRAND.freeShippingThreshold}</strong><span>Excludes eGift Cards</span></li>
        <li><strong>3&ndash;5 day delivery</strong><span>Via New Zealand Post</span></li>
        <li><strong>Tracked, door to door</strong><span>Emailed the moment it ships</span></li>
      </ul>
      <div class="poc-ship-cols">
        <div class="poc-ship-col">
          <h3>New Zealand</h3>
          <table class="poc-ship-rates">
            <thead><tr><th scope="col">Service</th><th scope="col">Delivery time</th><th scope="col">Cost</th></tr></thead>
            <tbody>
              <tr><td>Standard</td><td>3&ndash;5 business days</td><td>$10 (free for members, or over ${BRAND.freeShippingThreshold})</td></tr>
              <tr><td>Express</td><td>Next business day (Express Post network)</td><td>$15</td></tr>
            </tbody>
          </table>
          <p class="poc-ship-note">Sent via New Zealand Post or a courier, depending on your location. eGift Cards don't require shipping.</p>
        </div>
        <div class="poc-ship-col">
          <h3>International</h3>
          <p class="poc-ship-lead">Shown at checkout once you enter your address - added to your total before you pay.</p>
          <p class="poc-ship-note">Rates exclude any overseas duties and customs charges, which are the recipient's responsibility.</p>
        </div>
      </div>
    </div>
  `;

  function injectShippingLightboxStyleOnce() {
    if (document.getElementById('poc-ship-lightbox-style')) return;
    const style = document.createElement('style');
    style.id = 'poc-ship-lightbox-style';
    style.textContent = SHIPPING_LIGHTBOX_CSS;
    document.head.appendChild(style);
  }

  let lightboxLastFocused = null;

  function closeLightbox() {
    const overlay = document.getElementById('poc-lightbox-overlay');
    if (!overlay) return;
    overlay.remove();
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onLightboxKeydown);
    if (lightboxLastFocused && typeof lightboxLastFocused.focus === 'function') {
      lightboxLastFocused.focus();
    }
  }

  function onLightboxKeydown(e) {
    if (e.key === 'Escape') closeLightbox();
  }

  function openLightbox(url, title) {
    closeLightbox();
    lightboxLastFocused = document.activeElement;

    const overlay = document.createElement('div');
    overlay.id = 'poc-lightbox-overlay';
    overlay.addEventListener('mousedown', (e) => {
      if (e.target === overlay) closeLightbox();
    });

    const panel = document.createElement('div');
    panel.id = 'poc-lightbox-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');

    const header = document.createElement('div');
    header.id = 'poc-lightbox-header';
    const heading = document.createElement('h2');
    heading.id = 'poc-lightbox-title';
    heading.textContent = title;
    const closeBtn = document.createElement('button');
    closeBtn.id = 'poc-lightbox-close';
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', closeLightbox);
    header.appendChild(heading);
    header.appendChild(closeBtn);

    const body = document.createElement('div');
    body.id = 'poc-lightbox-body';
    body.className = 'poc-lightbox-loading';
    body.textContent = 'Loading…';

    panel.appendChild(header);
    panel.appendChild(body);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onLightboxKeydown);
    closeBtn.focus();

    if (url === BRAND.shippingOptionsUrl) {
      injectShippingLightboxStyleOnce();
      body.className = '';
      body.innerHTML = SHIPPING_LIGHTBOX_MARKUP;
      return;
    }

    fetch(url, { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.getElementById('MainContent') || doc.querySelector('main') || doc.body;
        content.querySelectorAll('script, style').forEach((el) => el.remove());

        body.className = '';
        body.innerHTML = '';
        body.appendChild(document.importNode(content, true));
      })
      .catch(() => {
        body.className = '';
        body.innerHTML = '';
        const fallback = document.createElement('p');
        const link = document.createElement('a');
        link.href = url;
        link.textContent = 'View this page';
        fallback.append('Sorry, we couldn’t load this here. ', link, '.');
        body.appendChild(fallback);
      });
  }

  function interceptAsLightbox(link, title) {
    link.addEventListener('click', (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      openLightbox(link.href, title);
    });
  }

  // ---------- Shipping & Returns accordion copy ----------
  //
  // NOTE (jockey.co.nz, checked 2026-09-18): the live NZ PDP theme does not
  // render a "Shipping & Returns" <details> accordion below the buy box at
  // all — this function's target simply doesn't exist on this page. It's
  // kept in (rather than deleted) so it activates automatically if a future
  // theme update adds one back, and it's a harmless no-op via pocRetryUntil
  // otherwise. Don't rely on this for returns-policy disclosure on NZ today;
  // the trust box no longer makes a returns claim at all (see the "Need it
  // sooner?" column above), so there's no false-claim risk from the gap.
  function rewriteShippingReturnsPanel() {
    if (document.getElementById('poc-shipping-returns-copy')) return;

    // The heading is always inside a <details>, so scanning every leaf
    // inside every <details> (a handful of accordion items) finds it just
    // as reliably as scanning every element on the whole page, at a
    // fraction of the cost — see jockey-pdp-suite.user.js v1.0.1 history.
    let details = null;
    for (const d of document.querySelectorAll('details')) {
      const hasHeading = [...d.querySelectorAll('*')].some(
        (el) => el.children.length === 0 && /shipping\s*&\s*returns/i.test(el.textContent)
      );
      if (hasHeading) {
        details = d;
        break;
      }
    }
    const panel = details && details.querySelector('.cc-accordion-item__panel');
    if (!panel) return;

    panel.innerHTML = '';
    panel.id = 'poc-shipping-returns-copy';

    const shippingP = document.createElement('p');
    const shippingLink = document.createElement('a');
    shippingLink.href = BRAND.shippingOptionsUrl;
    shippingLink.textContent = 'shipping options';
    interceptAsLightbox(shippingLink, 'Shipping options');
    shippingP.append(
      `Free standard shipping for Jockey members, and on all orders over ${BRAND.freeShippingThreshold} for everyone else ($10 under that). See our full `,
      shippingLink,
      ' for details.'
    );

    const returnsP = document.createElement('p');
    const returnsLink = document.createElement('a');
    returnsLink.href = BRAND.returnsPolicyUrl;
    returnsLink.textContent = 'returns and exchange policy';
    interceptAsLightbox(returnsLink, 'Returns & exchanges');
    returnsP.append(
      `Change your mind within ${BRAND.returnsWindowDays} days of delivery for a full refund or exchange, if items are unworn with tags attached — underwear (briefs) and hosiery can only be returned if faulty. Returns are at your own expense (a tracked service is recommended) and need a Return Authorisation Number from us first. Read our full `,
      returnsLink,
      '.'
    );

    panel.appendChild(shippingP);
    panel.appendChild(returnsP);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
